import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";

import {
    createModule,
    createModuleSection,
    deleteModuleSection,
    deleteModule,
    getModuleSectionsAdmin,
    getModulesAdminByAuthor,
    patchModuleSection,
    patchModule,
    setModuleEnabled,
} from "../../api/subjects";

import ModuleQuestionsEditorDialog from "./ModuleQuestionsEditorDialog";

const SECTION_ICON_OPTIONS = [
    { value: "test", label: "Тестові завдання" },
    { value: "interactive", label: "Інтерактивні вправи" },
    { value: "video", label: "Відео / самостійна робота" },
];

const normalize = (value) => String(value ?? "").trim();
const slugifySection = (value) =>
    normalize(value)
        .toLowerCase()
        .replace(/[^a-z0-9а-яіїєґ]+/gi, "-")
        .replace(/^-+|-+$/g, "");
const normalizeSection = (section) => {
    const title = normalize(section?.title);
    const sectionSlug = normalize(section?.sectionSlug || section?.slug) || slugifySection(title);

    return {
        ...section,
        sectionSlug,
        title,
        description: normalize(section?.description),
        iconKey: normalize(section?.iconKey) || "test",
        order: Number(section?.order ?? 0) || 0,
        enabled: section?.enabled !== false,
    };
};

const mergeSections = (moduleSections, catalogSections) => {
    const map = new Map();

    [...(Array.isArray(moduleSections) ? moduleSections : []), ...(Array.isArray(catalogSections) ? catalogSections : [])]
        .map(normalizeSection)
        .filter((section) => section.sectionSlug)
        .forEach((section) => {
            if (!map.has(section.sectionSlug)) {
                map.set(section.sectionSlug, section);
            }
        });

    return Array.from(map.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const ModulesAdminPanel = ({ classId, subjectSlug, authorSlug, onBack }) => {
    const [modules, setModules] = useState([]);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [moduleSlug, setModuleSlug] = useState("");
    const [title, setTitle] = useState("");
    const [order, setOrder] = useState(0);
    const [pdfPath, setPdfPath] = useState("");
    const [pdfFileName, setPdfFileName] = useState("");
    const [creating, setCreating] = useState(false);

    const [deletingSlug, setDeletingSlug] = useState("");
    const [deleteDialogModule, setDeleteDialogModule] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    const [sectionsDialogOpen, setSectionsDialogOpen] = useState(false);
    const [newSectionTitle, setNewSectionTitle] = useState("");
    const [newSectionDescription, setNewSectionDescription] = useState("");
    const [newSectionIconKey, setNewSectionIconKey] = useState("test");
    const [newSectionOrder, setNewSectionOrder] = useState(0);
    const [creatingSection, setCreatingSection] = useState(false);
    const [savingSectionSlug, setSavingSectionSlug] = useState("");
    const [deletingSectionSlug, setDeletingSectionSlug] = useState("");

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [savingQuestions, setSavingQuestions] = useState(false);

    const [snack, setSnack] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const headerSubtitle = useMemo(() => {
        const parts = [];
        if (classId) parts.push(`Клас: ${classId}`);
        if (subjectSlug) parts.push(`Предмет: ${subjectSlug}`);
        if (authorSlug) parts.push(`Автор: ${authorSlug}`);
        return parts.join(" • ");
    }, [classId, subjectSlug, authorSlug]);

    const availableSections = useMemo(
        () => (Array.isArray(sections) ? sections.map(normalizeSection).filter((section) => section.sectionSlug) : []),
        [sections]
    );

    const loadModules = useCallback(
        async (opts) => {
            const silent = Boolean(opts && opts.silent);
            if (!classId || !subjectSlug || !authorSlug) return;

            if (!silent) setLoading(true);
            try {
                const data = await getModulesAdminByAuthor(classId, subjectSlug, authorSlug);
                setModules(Array.isArray(data) ? data : []);
            } catch (e) {
                const msg =
                    e?.response?.data ||
                    e?.message ||
                    "Не вдалося завантажити список модулів";
                setSnack({ open: true, severity: "error", message: String(msg) });
                setModules([]);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [classId, subjectSlug, authorSlug]
    );

    const loadSections = useCallback(
        async (opts) => {
            const silent = Boolean(opts && opts.silent);
            if (!classId || !subjectSlug || !authorSlug) return;

            try {
                const data = await getModuleSectionsAdmin(classId, subjectSlug, authorSlug);
                setSections(Array.isArray(data) ? data.map(normalizeSection).filter((section) => section.sectionSlug) : []);
            } catch (e) {
                setSections([]);
                if (!silent) {
                    const msg = e?.response?.data || e?.message || "Не вдалося завантажити секції";
                    setSnack({ open: true, severity: "error", message: String(msg) });
                }
            }
        },
        [classId, subjectSlug, authorSlug]
    );

    useEffect(() => {
        loadModules();
        loadSections({ silent: true });
    }, [loadModules, loadSections]);

    function openDialog() {
        setModuleSlug("");
        setTitle("");
        setOrder(0);
        setPdfPath("");
        setPdfFileName("");
        setDialogOpen(true);
    }

    function closeDialog() {
        if (!creating) setDialogOpen(false);
    }

    async function onCreate() {
        const ms = String(moduleSlug || "").trim();
        const t = String(title || "").trim();

        if (!ms) {
            setSnack({ open: true, severity: "error", message: "Введіть moduleSlug" });
            return;
        }
        if (!t) {
            setSnack({ open: true, severity: "error", message: "Введіть назву модуля" });
            return;
        }

        setCreating(true);
        try {
            await createModule(classId, subjectSlug, authorSlug, {
                moduleSlug: ms,
                title: t,
                order: Number(order) || 0,
                pdfPath: String(pdfPath || "").trim() || null,
                pdfFileName: String(pdfFileName || "").trim() || null,
                questions: [],
                sections: [],
            });

            setSnack({ open: true, severity: "success", message: "Модуль додано" });
            setDialogOpen(false);
            await loadModules({ silent: true });
        } catch (e) {
            const status = e?.response?.status;
            const msg = e?.response?.data || e?.message || "Не вдалося додати модуль";
            setSnack({
                open: true,
                severity: "error",
                message: status ? `${status}: ${msg}` : String(msg),
            });
        } finally {
            setCreating(false);
        }
    }

    async function onToggleEnabled(mSlug, next) {
        try {
            await setModuleEnabled(classId, subjectSlug, authorSlug, mSlug, next);
            setModules((prev) =>
                prev.map((x) => (x.moduleSlug === mSlug ? { ...x, enabled: next } : x))
            );
            setSnack({
                open: true,
                severity: "success",
                message: next ? "Модуль увімкнено" : "Модуль вимкнено",
            });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося змінити статус";
            setSnack({ open: true, severity: "error", message: String(msg) });
        }
    }

    function openDeleteDialog(module) {
        if (!module?.moduleSlug) return;
        setDeleteDialogModule(module);
        setDeleteConfirmText("");
    }

    function closeDeleteDialog() {
        if (!deletingSlug) {
            setDeleteDialogModule(null);
            setDeleteConfirmText("");
        }
    }

    async function onDelete(mSlug) {
        if (!mSlug) return false;
        setDeletingSlug(mSlug);
        try {
            await deleteModule(classId, subjectSlug, authorSlug, mSlug);
            setModules((prev) => prev.filter((x) => x.moduleSlug !== mSlug));
            setSnack({ open: true, severity: "success", message: "Модуль видалено" });
            return true;
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося видалити модуль";
            setSnack({ open: true, severity: "error", message: String(msg) });
            return false;
        } finally {
            setDeletingSlug("");
        }
    }

    async function confirmDeleteModule() {
        const mSlug = deleteDialogModule?.moduleSlug;
        if (!mSlug || normalize(deleteConfirmText) !== "видалити") return;

        const deleted = await onDelete(mSlug);
        if (deleted) {
            setDeleteDialogModule(null);
            setDeleteConfirmText("");
        }
    }

    function openEditor(m) {
        setEditingModule(m);
        setEditorOpen(true);
    }

    function resetNewSectionForm() {
        setNewSectionTitle("");
        setNewSectionDescription("");
        setNewSectionIconKey("test");
        setNewSectionOrder(0);
    }

    async function onCreateSection() {
        const titleValue = normalize(newSectionTitle);
        if (!titleValue) {
            setSnack({ open: true, severity: "error", message: "Введіть назву секції" });
            return;
        }

        setCreatingSection(true);
        try {
            await createModuleSection(classId, subjectSlug, authorSlug, {
                title: titleValue,
                description: normalize(newSectionDescription),
                iconKey: newSectionIconKey || "test",
                order: Number(newSectionOrder) || 0,
                enabled: true,
            });
            setSnack({ open: true, severity: "success", message: "Секцію додано" });
            resetNewSectionForm();
            await loadSections({ silent: true });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося додати секцію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setCreatingSection(false);
        }
    }

    async function onPatchSection(section, patch) {
        const sectionSlug = section?.sectionSlug;
        if (!sectionSlug) return;

        setSavingSectionSlug(sectionSlug);
        try {
            await patchModuleSection(classId, subjectSlug, authorSlug, sectionSlug, patch);
            await loadSections({ silent: true });
            setSnack({ open: true, severity: "success", message: "Секцію оновлено" });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося оновити секцію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setSavingSectionSlug("");
        }
    }

    async function onDeleteSection(sectionSlug) {
        if (!sectionSlug) return;

        setDeletingSectionSlug(sectionSlug);
        try {
            await deleteModuleSection(classId, subjectSlug, authorSlug, sectionSlug);
            await loadSections({ silent: true });
            setSnack({ open: true, severity: "success", message: "Секцію видалено" });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося видалити секцію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setDeletingSectionSlug("");
        }
    }

    function closeEditor() {
        if (!savingQuestions) setEditorOpen(false);
    }

    async function onSaveQuestions(nextQuestions, nextSections) {
        if (!editingModule?.moduleSlug) return;
        setSavingQuestions(true);
        try {
            await patchModule(
                classId,
                subjectSlug,
                authorSlug,
                editingModule.moduleSlug,
                {
                    questions: nextQuestions,
                    sections: nextSections,
                }
            );

            setModules((prev) =>
                prev.map((x) =>
                    x.moduleSlug === editingModule.moduleSlug
                        ? { ...x, questions: nextQuestions, sections: nextSections }
                        : x
                )
            );

            setSnack({ open: true, severity: "success", message: "Контент збережено" });
            setEditorOpen(false);
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося зберегти контент";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setSavingQuestions(false);
        }
    }

    return (
        <Container disableGutters maxWidth={false} sx={{ mt: 2 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    bgcolor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 2,
                    py: 2,
                }}
            >
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={onBack} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" fontWeight={700}>
                            Модулі
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {headerSubtitle}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="outlined"
                        onClick={() => setSectionsDialogOpen(true)}
                        disabled={!classId || !subjectSlug || !authorSlug}
                    >
                        Секції
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openDialog}
                        disabled={!classId || !subjectSlug || !authorSlug}
                    >
                        Додати
                    </Button>
                </Stack>
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            display: "none",
                            bgcolor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            mx: -2,
                            px: 2,
                            py: 2,
                        }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton onClick={onBack} size="small">
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h5" fontWeight={700}>
                                    Модулі
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {headerSubtitle}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined"
                                onClick={() => setSectionsDialogOpen(true)}
                                disabled={!classId || !subjectSlug || !authorSlug}
                            >
                                Секції
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openDialog}
                                disabled={!classId || !subjectSlug || !authorSlug}
                            >
                                Додати
                            </Button>
                        </Stack>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                        <TableContainer
                            component={Box}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Назва</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>moduleSlug</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 110 }}>Порядок</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>PDF</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Активний</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 190 }}>Дії</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <CircularProgress size={18} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Завантаження...
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ) : modules.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ color: "text.secondary" }}>
                                                Список порожній
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        modules.map((m) => {
                                            const key =
                                                m.id ||
                                                `${m.classId}-${m.subjectSlug}-${m.authorSlug}-${m.moduleSlug}`;
                                            const pdfText = m.pdfPath ? `${m.pdfFileName || "PDF"}` : "—";
                                            const hasQuestions = Array.isArray(m.questions) && m.questions.length > 0;

                                            return (
                                                <TableRow
                                                    key={key}
                                                    sx={{ ...(!m.enabled ? { opacity: 0.6 } : {}) }}
                                                >
                                                    <TableCell sx={!m.enabled ? { textDecoration: "line-through" } : undefined}>
                                                        {m.title}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                                                            {m.moduleSlug}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{m.order ?? 0}</TableCell>
                                                    <TableCell>{pdfText}</TableCell>
                                                    <TableCell>
                                                        <Switch
                                                            checked={Boolean(m.enabled)}
                                                            onChange={(e) => onToggleEnabled(m.moduleSlug, e.target.checked)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <IconButton size="small" onClick={() => openEditor(m)} disabled={!m.moduleSlug}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>

                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openDeleteDialog(m)}
                                                                disabled={deletingSlug === m.moduleSlug}
                                                            >
                                                                {deletingSlug === m.moduleSlug ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <DeleteIcon fontSize="small" />
                                                                )}
                                                            </IconButton>

                                                            <Typography variant="caption" color="text.secondary">
                                                                {hasQuestions ? "є контент" : "порожньо"}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Додати модуль</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="moduleSlug"
                            value={moduleSlug}
                            onChange={(e) => setModuleSlug(e.target.value)}
                            placeholder="module001"
                            fullWidth
                        />
                        <TextField
                            label="Назва"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Наприклад: Розділ 1.1..."
                            fullWidth
                        />
                        <TextField
                            label="Порядок"
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="pdfPath"
                            value={pdfPath}
                            onChange={(e) => setPdfPath(e.target.value)}
                            placeholder="/pdf/ukr_avr_8/module01.pdf"
                            fullWidth
                        />
                        <TextField
                            label="pdfFileName"
                            value={pdfFileName}
                            onChange={(e) => setPdfFileName(e.target.value)}
                            placeholder="Завдання до 01.pdf"
                            fullWidth
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={creating}>
                        Скасувати
                    </Button>
                    <Button variant="contained" onClick={onCreate} disabled={creating}>
                        {creating ? <CircularProgress size={18} /> : "Додати"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteDialogModule)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>Видалити модуль?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Модуль "{deleteDialogModule?.title || deleteDialogModule?.moduleSlug}" буде видалено
                            безповоротно. Щоб підтвердити дію, введіть слово "видалити".
                        </Typography>
                        <TextField
                            label='Введіть "видалити"'
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            autoFocus
                            fullWidth
                            disabled={Boolean(deletingSlug)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDeleteDialog} disabled={Boolean(deletingSlug)}>
                        Скасувати
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDeleteModule}
                        disabled={Boolean(deletingSlug) || normalize(deleteConfirmText) !== "видалити"}
                    >
                        {deletingSlug === deleteDialogModule?.moduleSlug ? <CircularProgress size={18} /> : "OK"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={sectionsDialogOpen} onClose={() => setSectionsDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Секції модулів</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                Додати секцію
                            </Typography>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                                <TextField
                                    label="Назва"
                                    value={newSectionTitle}
                                    onChange={(e) => setNewSectionTitle(e.target.value)}
                                    placeholder="Тестові завдання"
                                    fullWidth
                                />
                                <TextField
                                    label="Опис"
                                    value={newSectionDescription}
                                    onChange={(e) => setNewSectionDescription(e.target.value)}
                                    placeholder="Питання з варіантами відповідей"
                                    fullWidth
                                />
                                <TextField
                                    select
                                    label="Іконка"
                                    value={newSectionIconKey}
                                    onChange={(e) => setNewSectionIconKey(e.target.value)}
                                    sx={{ minWidth: { md: 190 } }}
                                >
                                    {SECTION_ICON_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Порядок"
                                    type="number"
                                    value={newSectionOrder}
                                    onChange={(e) => setNewSectionOrder(e.target.value)}
                                    sx={{ width: { md: 120 } }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={onCreateSection}
                                    disabled={creatingSection}
                                    sx={{ minWidth: 120 }}
                                >
                                    {creatingSection ? <CircularProgress size={18} /> : "Додати"}
                                </Button>
                            </Stack>
                        </Box>

                        <TableContainer
                            component={Box}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Назва</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Опис</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 190 }}>Іконка</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Порядок</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Активна</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 170 }}>Дії</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sections.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ color: "text.secondary" }}>
                                                Секцій ще немає
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sections.map((section) => (
                                            <TableRow key={section.sectionSlug || section.slug || section.id}>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={section.title || ""}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, title: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={section.description || ""}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, description: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={section.iconKey || "test"}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, iconKey: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                        fullWidth
                                                    >
                                                        {SECTION_ICON_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={section.order ?? 0}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, order: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={section.enabled !== false}
                                                        onChange={(e) =>
                                                            onPatchSection(section, { enabled: e.target.checked })
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() =>
                                                                onPatchSection(section, {
                                                                    title: normalize(section.title),
                                                                    description: normalize(section.description),
                                                                    iconKey: section.iconKey || "test",
                                                                    order: Number(section.order) || 0,
                                                                })
                                                            }
                                                            disabled={savingSectionSlug === section.sectionSlug}
                                                        >
                                                            {savingSectionSlug === section.sectionSlug ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                "OK"
                                                            )}
                                                        </Button>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onDeleteSection(section.sectionSlug)}
                                                            disabled={deletingSectionSlug === section.sectionSlug}
                                                        >
                                                            {deletingSectionSlug === section.sectionSlug ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <DeleteIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSectionsDialogOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>

            <ModuleQuestionsEditorDialog
                open={editorOpen}
                onClose={closeEditor}
                initialQuestions={editingModule?.questions || []}
                initialSections={mergeSections(editingModule?.sections || [], availableSections)}
                title={editingModule?.moduleSlug ? `Контент: ${editingModule.moduleSlug}` : "Контент модуля"}
                onSave={onSaveQuestions}
            />

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ModulesAdminPanel;
